/* MTL Shoppable Video — native replacement for the Whatmore app.
 *
 * Three pieces:
 *   <mtl-video-strip>   poster tiles that open the player on tap
 *   <mtl-video-bubble>  the floating pill, pinned bottom-right on every page
 *   MTLPlayer           one fullscreen story player, built on first open
 *
 * Tiles ship as lazy <img> posters. A muted preview is attached once a tile is
 * actually on screen and torn down again when it scrolls away — see Autoplay
 * below. Add to cart reuses the theme's own cart-drawer renderContents()
 * contract, same as assets/product-form.js.
 */
(function () {
  'use strict';

  if (window.__mtlVideoInit) return;
  window.__mtlVideoInit = true;

  var SVG = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z"/></svg>',
    on: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 2.2v2.1a7.7 7.7 0 0 1 0 15.4v2.1a9.8 9.8 0 0 0 0-19.6z"/></svg>',
    off: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 12A4.5 4.5 0 0 0 14 8v2.2l2.5 2.5V12zM19 12a7 7 0 0 1-1 3.6l1.5 1.5A9 9 0 0 0 21 12a9 9 0 0 0-7-8.8v2.1A7 7 0 0 1 19 12zM4.3 3 3 4.3 7.7 9H3v6h4l5 5v-6.7l4.3 4.3a7 7 0 0 1-2.3 1.2v2.1a9 9 0 0 0 3.8-1.8L19.7 21 21 19.7 4.3 3zM12 4 9.9 6.1 12 8.2V4z"/></svg>',
    share: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 16.1c-.8 0-1.5.3-2 .8l-7-4.1c0-.2.1-.5.1-.8s0-.6-.1-.8l7-4.1c.5.5 1.2.8 2 .8a2.9 2.9 0 1 0-2.9-2.9c0 .3 0 .6.1.8L8.2 9.9a2.9 2.9 0 1 0 0 4.2l7.1 4.1c0 .3-.1.5-.1.8a2.9 2.9 0 1 0 2.9-2.9z"/></svg>'
  };

  var money = function (v) { return v; }; // Liquid pre-formats prices
  var noop = function () {};

  /* ----------------------------------------------------------- autoplay bits */

  // Every tile that is actually on screen plays, and plays its clip in full.
  // That is a deliberate call by the store owner: it costs real bandwidth
  // (roughly 2-4 MB per clip, and these are full-length reels) and the
  // IntersectionObserver below is the only thing bounding it. Turn it off per
  // section in the theme editor rather than reintroducing a cap here.
  var VISIBLE_ENOUGH = 0.5;

  function canAutoplay() {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    } catch (e) { /* no matchMedia */ }
    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return false;
    return true;
  }

  // Shopify's processed videos carry a 480p progressive rendition next to the
  // 720p one we store — same path, different token, ~40% lighter and plenty
  // for a thumbnail. The five legacy whatmore_1080p_* files have no renditions
  // and are returned unchanged; start() falls back if a guess ever 404s.
  function lowRes(url) {
    return String(url || '').replace(/\.HD-720p-[\d.]+Mbps-/, '.SD-480p-0.9Mbps-');
  }

  function whenIdle(fn) {
    var run = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 800);
    };
    // never compete with the page's own load; previews are decoration
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  var Autoplay = {
    recs: [],
    io: null,
    frame: false,
    paused: false,

    register: function (tile, item) {
      if (!item || !item.mp4 || !window.IntersectionObserver || !canAutoplay()) return;
      var rec = { tile: tile, item: item, vid: null, visible: false };
      tile.__mtlAuto = rec;
      this.recs.push(rec);
      this.observer().observe(tile);
      this.bindScroll(tile);
    },

    observer: function () {
      var self = this;
      if (!this.io) {
        this.io = new IntersectionObserver(function (list) {
          list.forEach(function (e) {
            if (e.target.__mtlAuto) e.target.__mtlAuto.visible = e.isIntersecting;
          });
          self.schedule();
        }, { threshold: VISIBLE_ENOUGH });
      }
      return this.io;
    },

    // Belt and braces for the horizontal scrollers: the observer already fires
    // as tiles cross the threshold, but a scroll that settles mid-frame can
    // leave a tile visible and unstarted.
    bindScroll: function (tile) {
      var row = tile.closest('.mtl-vs__row');
      if (!row || row.__mtlBound) return;
      row.__mtlBound = true;
      var self = this;
      row.addEventListener('scroll', function () { self.schedule(); }, { passive: true });
    },

    schedule: function () {
      var self = this;
      if (this.frame) return;
      this.frame = true;
      requestAnimationFrame(function () {
        self.frame = false;
        self.sync();
      });
    },

    sync: function () {
      var self = this;
      if (this.paused) {
        this.recs.forEach(function (r) { self.stop(r); });
        return;
      }

      this.recs.forEach(function (r) {
        if (r.visible) self.start(r);
        else self.stop(r);
      });
    },

    start: function (rec) {
      if (rec.vid) {
        if (rec.vid.paused) rec.vid.play().catch(noop);
        return;
      }
      var v = document.createElement('video');
      v.className = 'mtl-vs__vid';
      // both the property and the attribute — iOS honours the attribute
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.loop = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('loop', '');
      v.setAttribute('disableremoteplayback', '');
      // plays the clip end to end, then loops
      v.preload = 'auto';

      var img = rec.tile.querySelector('img');
      if (img) v.poster = img.currentSrc || img.src;
      // the crop span is what actually rounds the corners — see the CSS note
      var host = rec.tile.querySelector('.mtl-vs__crop') || rec.tile;

      var small = lowRes(rec.item.mp4);
      v.addEventListener('error', function () {
        // a missing 480p rendition should degrade, not blank the tile
        if (small !== rec.item.mp4 && v.src !== rec.item.mp4) {
          v.src = rec.item.mp4;
          v.play().catch(noop);
        }
      });
      v.addEventListener('playing', function () { rec.tile.classList.add('is-playing'); });

      v.src = small;
      host.appendChild(v);
      rec.vid = v;
      var p = v.play();
      if (p && p.catch) p.catch(noop);
    },

    stop: function (rec) {
      if (!rec.vid) return;
      rec.vid.pause();
      rec.vid.removeAttribute('src');
      rec.vid.load(); // release the buffer
      rec.vid.remove();
      rec.vid = null;
      rec.tile.classList.remove('is-playing');
    },

    unregister: function (tile) {
      var rec = tile && tile.__mtlAuto;
      if (!rec) return;
      this.stop(rec);
      if (this.io) this.io.unobserve(tile);
      var at = this.recs.indexOf(rec);
      if (at > -1) this.recs.splice(at, 1);
      delete tile.__mtlAuto;
    },

    setPaused: function (on) {
      this.paused = on;
      this.sync();
    }
  };

  document.addEventListener('visibilitychange', function () {
    Autoplay.setPaused(document.hidden);
  });

  /* ------------------------------------------------------------ the player */

  var MTLPlayer = {
    list: [],
    i: 0,
    muted: false,
    root: null,
    opener: null,

    build: function () {
      if (this.root) return;
      var el = document.createElement('div');
      el.className = 'mtl-vp';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Shoppable video');
      el.hidden = true;
      el.innerHTML =
        '<div class="mtl-vp__stage">' +
        '<video class="mtl-vp__video" playsinline preload="none" x-webkit-airplay="allow"></video>' +
        '<div class="mtl-vp__spin" hidden></div>' +
        '<button class="mtl-vp__nav mtl-vp__nav--prev" aria-label="Previous video"></button>' +
        '<button class="mtl-vp__nav mtl-vp__nav--next" aria-label="Next video"></button>' +
        '<div class="mtl-vp__bars"></div>' +
        '<div class="mtl-vp__top">' +
        '<button class="mtl-vp__btn mtl-vp__sound" aria-label="Toggle sound">' + SVG.on + '</button>' +
        '<button class="mtl-vp__btn mtl-vp__close" aria-label="Close">' + SVG.close + '</button>' +
        '</div>' +
        '<div class="mtl-vp__rail">' +
        '<button class="mtl-vp__btn mtl-vp__share" aria-label="Share">' + SVG.share + '</button>' +
        '<span>Share</span>' +
        '</div>' +
        '<div class="mtl-vp__cards"></div>' +
        '</div>';
      document.body.appendChild(el);

      this.root = el;
      this.stage = el.querySelector('.mtl-vp__stage');
      this.video = el.querySelector('.mtl-vp__video');
      this.bars = el.querySelector('.mtl-vp__bars');
      this.cards = el.querySelector('.mtl-vp__cards');
      this.spin = el.querySelector('.mtl-vp__spin');
      this.soundBtn = el.querySelector('.mtl-vp__sound');

      var self = this;
      el.querySelector('.mtl-vp__close').addEventListener('click', function () { self.close(); });
      el.querySelector('.mtl-vp__nav--prev').addEventListener('click', function () { self.go(-1); });
      el.querySelector('.mtl-vp__nav--next').addEventListener('click', function () { self.go(1); });
      this.soundBtn.addEventListener('click', function () { self.toggleSound(); });
      el.querySelector('.mtl-vp__share').addEventListener('click', function () { self.share(); });

      this.video.addEventListener('timeupdate', function () { self.tick(); });
      this.video.addEventListener('ended', function () { self.go(1); });
      this.video.addEventListener('waiting', function () { self.spin.hidden = false; });
      this.video.addEventListener('playing', function () { self.spin.hidden = true; });
      this.video.addEventListener('error', function () { self.spin.hidden = true; });
      // tapping the video itself pauses/resumes
      this.video.addEventListener('click', function () {
        if (self.video.paused) self.video.play(); else self.video.pause();
      });

      // vertical swipe moves between videos, same gesture as the app
      var y0 = null, x0 = null;
      this.stage.addEventListener('touchstart', function (e) {
        y0 = e.touches[0].clientY; x0 = e.touches[0].clientX;
      }, { passive: true });
      this.stage.addEventListener('touchend', function (e) {
        if (y0 === null) return;
        var dy = e.changedTouches[0].clientY - y0;
        var dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) self.go(dy < 0 ? 1 : -1);
        y0 = x0 = null;
      }, { passive: true });

      this.onKey = function (e) {
        if (self.root.hidden) return;
        if (e.key === 'Escape') self.close();
        else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') self.go(1);
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') self.go(-1);
      };
      document.addEventListener('keydown', this.onKey);
    },

    open: function (list, index, opener) {
      this.build();
      this.list = list;
      this.i = index || 0;
      this.opener = opener || null;
      // remember the visitor's sound choice for the session
      try {
        this.muted = sessionStorage.getItem('mtlVideoMuted') === '1';
      } catch (e) { /* private mode */ }
      this.root.hidden = false;
      document.body.classList.add('mtl-vp-open');
      Autoplay.setPaused(true); // nothing should play behind the fullscreen view
      this.renderBars();
      this.load();
      this.root.querySelector('.mtl-vp__close').focus();
    },

    close: function () {
      if (!this.root) return;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load(); // release the buffer
      this.root.hidden = true;
      document.body.classList.remove('mtl-vp-open');
      Autoplay.setPaused(false);
      var hint = this.root.querySelector('.mtl-vp__hint');
      if (hint) hint.remove();
      if (this.opener && this.opener.focus) this.opener.focus();
    },

    renderBars: function () {
      var h = '';
      for (var n = 0; n < this.list.length; n++) h += '<div class="mtl-vp__bar"><i></i></div>';
      this.bars.innerHTML = h;
      // only worth showing segments when there is more than one video
      this.bars.style.display = this.list.length > 1 ? '' : 'none';
    },

    load: function () {
      var item = this.list[this.i];
      if (!item) return;

      var src = (window.innerWidth >= 750 && item.mp4hd) ? item.mp4hd : item.mp4;
      this.video.poster = item.poster || '';
      this.video.src = src;
      this.video.muted = this.muted;
      this.video.currentTime = 0;
      this.soundBtn.innerHTML = this.muted ? SVG.off : SVG.on;
      this.spin.hidden = false;

      var self = this;
      var p = this.video.play();
      if (p && p.catch) {
        p.catch(function () {
          // autoplay with sound was refused — fall back to muted
          self.video.muted = true;
          self.muted = true;
          self.soundBtn.innerHTML = SVG.off;
          self.video.play().catch(function () { self.spin.hidden = true; });
        });
      }

      // mark earlier segments complete, reset later ones
      var bars = this.bars.children;
      for (var n = 0; n < bars.length; n++) {
        bars[n].classList.toggle('mtl-vp__bar--done', n < this.i);
        if (n > this.i) bars[n].firstChild.style.width = '0';
      }

      this.renderCards(item);

      if (this.list.length > 1 && !this.root.querySelector('.mtl-vp__hint')) {
        var hint = document.createElement('div');
        hint.className = 'mtl-vp__hint';
        hint.textContent = 'swipe for more';
        this.stage.appendChild(hint);
      }
    },

    tick: function () {
      var bar = this.bars.children[this.i];
      if (!bar || !this.video.duration) return;
      bar.firstChild.style.width = (this.video.currentTime / this.video.duration * 100) + '%';
    },

    go: function (step) {
      var next = this.i + step;
      if (next < 0) next = 0;
      if (next > this.list.length - 1) return this.close(); // past the end: dismiss
      if (next === this.i) return;
      this.i = next;
      this.load();
    },

    toggleSound: function () {
      this.muted = !this.muted;
      this.video.muted = this.muted;
      this.soundBtn.innerHTML = this.muted ? SVG.off : SVG.on;
      try {
        sessionStorage.setItem('mtlVideoMuted', this.muted ? '1' : '0');
      } catch (e) { /* private mode */ }
    },

    share: function () {
      var item = this.list[this.i] || {};
      var p = (item.products && item.products[0]) || {};
      var url = p.url ? location.origin + p.url : location.href;
      var data = { title: p.title || item.title || document.title, url: url };
      if (navigator.share) {
        navigator.share(data).catch(function () { /* dismissed */ });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url);
        this.toast('Link copied');
      }
    },

    toast: function (msg) {
      var t = document.createElement('div');
      t.className = 'mtl-vp__hint';
      t.textContent = msg;
      this.stage.appendChild(t);
      setTimeout(function () { t.remove(); }, 2200);
    },

    renderCards: function (item) {
      var self = this;
      this.cards.innerHTML = '';
      (item.products || []).forEach(function (p) {
        var card = document.createElement('div');
        card.className = 'mtl-vp__card';

        var price = '<span>' + money(p.price) + '</span>';
        if (p.compare) price += '<s>' + money(p.compare) + '</s>';
        if (p.off) price += '<span class="mtl-vs__off">' + p.off + '% OFF</span>';

        card.innerHTML =
          '<img src="' + p.img + '" alt="" loading="lazy" width="56" height="72">' +
          '<div class="mtl-vp__info">' +
          '<a class="mtl-vp__name" href="' + p.url + '">' + p.title + '</a>' +
          '<div class="mtl-vp__cost">' + price + '</div>' +
          '</div>';

        var btn = document.createElement('button');
        btn.type = 'button';
        if (p.available && p.variant) {
          btn.className = 'mtl-vp__cta';
          btn.textContent = 'Add to cart';
          btn.addEventListener('click', function () { self.addToCart(p, btn); });
        } else {
          btn.className = 'mtl-vp__cta mtl-vp__cta--sold';
          btn.textContent = 'Sold out';
          btn.disabled = true;
        }
        card.appendChild(btn);
        self.cards.appendChild(card);
      });
    },

    addToCart: function (p, btn) {
      var self = this;
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      var body = new FormData();
      body.append('id', p.variant);
      body.append('quantity', 1);
      if (cart && cart.getSectionsToRender) {
        body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        body.append('sections_url', window.location.pathname);
      }

      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) throw new Error(res.description || res.message || 'Add to cart failed');
          btn.textContent = 'Added ✓';
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'mtl-shoppable-video',
              productVariantId: p.variant,
              cartData: res
            });
          }
          if (cart && cart.renderContents) {
            // The drawer keeps its empty-state class when the cart was empty,
            // which hides the new line item even though it is in the DOM.
            // assets/product-form.js clears it the same way.
            cart.classList.remove('is-empty');
            var inner = cart.querySelector('.drawer__inner');
            if (inner) inner.classList.remove('is-empty');

            // closing first keeps the drawer from opening behind the overlay
            self.close();
            cart.renderContents(res);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function (e) {
          btn.textContent = 'Try again';
          self.toast(e.message || 'Could not add to cart');
          setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 2500);
        })
        .finally(function () {
          setTimeout(function () { if (btn.isConnected) btn.disabled = false; }, 1200);
        });
    }
  };

  window.MTLPlayer = MTLPlayer;

  /* ------------------------------------------------------------ the strips */

  function readVideos(host) {
    var script = host.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      var list = JSON.parse(script.textContent);
      return list && list.length ? list : null;
    } catch (e) {
      return null;
    }
  }

  if (!customElements.get('mtl-video-strip')) {
    customElements.define('mtl-video-strip', class extends HTMLElement {
      connectedCallback() {
        var videos = readVideos(this);
        if (!videos) return;
        this.videos = videos;

        var self = this;
        var tiles = [];
        this.querySelectorAll('[data-mtl-index]').forEach(function (tile) {
          tiles.push(tile);
          tile.addEventListener('click', function (e) {
            e.preventDefault();
            MTLPlayer.open(self.videos, parseInt(tile.dataset.mtlIndex, 10) || 0, tile);
          });
        });

        if (this.dataset.autoplay) {
          whenIdle(function () {
            tiles.forEach(function (tile) {
              Autoplay.register(tile, self.videos[parseInt(tile.dataset.mtlIndex, 10) || 0]);
            });
            Autoplay.schedule();
          });
        }
      }
    });
  }

  /* ------------------------------------------------------- the floating bubble */

  if (!customElements.get('mtl-video-bubble')) {
    customElements.define('mtl-video-bubble', class extends HTMLElement {
      connectedCallback() {
        var videos = readVideos(this);
        if (!videos) return;
        this.videos = videos;

        // once dismissed, stay gone for the rest of the visit
        try {
          if (sessionStorage.getItem('mtlBubbleHidden') === '1') {
            this.remove();
            return;
          }
        } catch (e) { /* private mode */ }

        var self = this;
        var open = this.querySelector('.mtl-vb__open');
        var close = this.querySelector('.mtl-vb__close');

        open.addEventListener('click', function () {
          MTLPlayer.open(self.videos, 0, open);
        });

        close.addEventListener('click', function (e) {
          e.stopPropagation();
          try {
            sessionStorage.setItem('mtlBubbleHidden', '1');
          } catch (err) { /* private mode */ }
          Autoplay.unregister(open);
          self.remove();
        });

        // slide in after paint so it never lands inside the LCP frame
        requestAnimationFrame(function () { self.classList.add('is-in'); });

        if (this.dataset.autoplay) {
          whenIdle(function () {
            Autoplay.register(open, self.videos[0]);
            Autoplay.schedule();
          });
        }
      }
    });
  }
})();
